import { useEffect, useState } from 'react';
import { LuFlaskConical, LuPlus, LuRotateCcw, LuTerminal, LuX } from 'react-icons/lu';
import { Tabs } from '../../ui/Navigation';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Feedback';
import { VerdictLabel } from '../../ui/Labels';
import { useStrings } from '../../tenant/TenantProvider';
import { cx } from '../../ui/cx';
import { formatMemory } from '../../lib/format';

const MAX_CASES = 10;
const ACCEPTED = 3;
const COMPILATION_ERROR = 6;

function Output({ label, value, tone }) {
  return (
    <div className="console-output">
      <p className="console-output__label">{label}</p>
      <pre className={cx('console-output__value', tone && `console-output__value--${tone}`)}>{value === '' || value == null ? ' ' : value}</pre>
    </div>
  );
}

function RunResult({ run }) {
  const t = useStrings();
  const [active, setActive] = useState(0);
  const result = run.result;
  const cases = Array.isArray(result.test_cases) ? result.test_cases : [result];
  const inputs = run.cases ?? [];
  const passed = cases.filter((item) => item?.status?.id === ACCEPTED).length;
  const compileError = cases.find((item) => item?.status?.id === COMPILATION_ERROR);
  const firstFailure = cases.find((item) => item?.status?.id !== ACCEPTED);

  useEffect(() => setActive(0), [result]);

  const current = cases[active];
  const input = inputs[active];

  return (
    <div className="console-result">
      <div className="console-result__summary">
        <span className={cx('console-result__headline', firstFailure ? 'verdict--rejected' : 'verdict--accepted')}>
          {firstFailure ? firstFailure.status?.description ?? t('verdicts.Wrong Answer') : t('verdicts.Accepted')}
        </span>
        <span className="muted">{t('workspace.console.passed', { passed, total: cases.length })}</span>
      </div>
      {compileError ? (
        <Output label={t('workspace.console.compilerOutput')} value={compileError.compile_output} tone="danger" />
      ) : (
        <>
          <div className="case-tabs" role="tablist" aria-label={t('workspace.console.testcases')}>
            {cases.map((item, index) => (
              <button
                key={index}
                type="button"
                role="tab"
                aria-selected={active === index}
                className="case-tab"
                onClick={() => setActive(index)}
              >
                <span className={cx('case-tab__dot', item?.status?.id === ACCEPTED ? 'is-pass' : 'is-fail')} aria-hidden="true" />
                {t('workspace.console.case', { number: index + 1 })}
              </button>
            ))}
          </div>
          {current && (
            <div className="console-result__case">
              {input && <Output label={t('workspace.console.input')} value={input.input} />}
              <Output
                label={t('workspace.console.yourOutput')}
                value={current.stdout}
                tone={current.status?.id === ACCEPTED ? undefined : 'danger'}
              />
              {input && <Output label={t('workspace.console.expected')} value={input.output} />}
              {current.stderr && <Output label={t('workspace.console.error')} value={current.stderr} tone="danger" />}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SubmitResult({ submission, acceptedNote }) {
  const t = useStrings();
  const result = submission.result;
  const accepted = result.verdict === 'Accepted';
  const memory = formatMemory(result.memoryUsed);
  return (
    <div className="console-result">
      <div className="console-result__summary">
        <VerdictLabel verdict={result.verdict} className="console-result__headline" />
        {result.executionTime && <span className="muted">{t('workspace.console.runtime', { value: result.executionTime })}</span>}
        {memory && <span className="muted">{t('workspace.console.memory', { value: memory })}</span>}
      </div>
      {accepted && (
        <p className="console-result__note">
          {t('workspace.console.hiddenAccepted')}
          {acceptedNote ? ` ${acceptedNote}` : ''}
        </p>
      )}
      {!accepted && result.failedTestCase && <p>{t('workspace.console.failedOn', { number: result.failedTestCase })}</p>}
      {result.error && (
        <Output
          label={result.verdict === 'Compilation Error' ? t('workspace.console.compilerOutput') : t('workspace.console.error')}
          value={result.error}
          tone="danger"
        />
      )}
    </div>
  );
}

export function RunConsole({ testCases, onTestCasesChange, onResetCases, tab, onTabChange, run, submission, lastAction, readOnly, acceptedNote }) {
  const t = useStrings();
  const [activeCase, setActiveCase] = useState(0);
  const safeIndex = Math.min(activeCase, Math.max(testCases.length - 1, 0));
  const current = testCases[safeIndex];
  const showing = lastAction === 'submit' ? submission : run;

  const updateCase = (field, value) => {
    onTestCasesChange(testCases.map((item, index) => (index === safeIndex ? { ...item, [field]: value } : item)));
  };

  const addCase = () => {
    if (testCases.length >= MAX_CASES) return;
    onTestCasesChange([...testCases, { input: current?.input ?? '', output: '' }]);
    setActiveCase(testCases.length);
  };

  const removeCase = (index) => {
    if (testCases.length <= 1) return;
    onTestCasesChange(testCases.filter((_, position) => position !== index));
    setActiveCase(Math.max(0, index - 1));
  };

  return (
    <section className="pane run-console">
      <div className="pane__header">
        <Tabs
          label={t('workspace.console.testcases')}
          value={tab}
          onChange={onTabChange}
          tabs={[
            { id: 'testcases', label: t('workspace.console.testcases'), icon: LuFlaskConical },
            { id: 'result', label: t('workspace.console.result'), icon: LuTerminal },
          ]}
        />
        {tab === 'testcases' && !readOnly && (
          <Button size="sm" variant="ghost" icon={LuRotateCcw} onClick={onResetCases}>
            {t('workspace.console.restore')}
          </Button>
        )}
      </div>
      <div className="pane__body">
        {tab === 'testcases' && (
          <div className="testcases">
            <div className="case-tabs">
              {testCases.map((_, index) => (
                <span key={index} className="case-tab-wrap">
                  <button type="button" className="case-tab" aria-pressed={safeIndex === index} onClick={() => setActiveCase(index)}>
                    {t('workspace.console.case', { number: index + 1 })}
                  </button>
                  {testCases.length > 1 && !readOnly && (
                    <button
                      type="button"
                      className="case-tab__remove"
                      aria-label={t('workspace.console.removeCase', { number: index + 1 })}
                      onClick={() => removeCase(index)}
                    >
                      <LuX aria-hidden="true" />
                    </button>
                  )}
                </span>
              ))}
              {testCases.length < MAX_CASES && !readOnly && (
                <button type="button" className="icon-btn icon-btn--sm" aria-label={t('workspace.console.addCase')} title={t('workspace.console.addCase')} onClick={addCase}>
                  <LuPlus aria-hidden="true" />
                </button>
              )}
            </div>
            {current && (
              <div className="testcases__fields">
                <label className="field">
                  <span className="field__label">{t('workspace.console.input')}</span>
                  <textarea
                    className="textarea textarea--code"
                    value={current.input}
                    onChange={(event) => updateCase('input', event.target.value)}
                    readOnly={readOnly}
                    rows={3}
                    spellCheck={false}
                  />
                </label>
                <label className="field">
                  <span className="field__label">{t('workspace.console.expected')}</span>
                  <textarea
                    className="textarea textarea--code"
                    value={current.output}
                    onChange={(event) => updateCase('output', event.target.value)}
                    readOnly={readOnly}
                    rows={2}
                    spellCheck={false}
                  />
                </label>
              </div>
            )}
          </div>
        )}

        {tab === 'result' && (
          <>
            {!showing && <p className="console-empty">{t('workspace.console.empty')}</p>}
            {showing?.status === 'pending' && (
              <p className="console-pending">
                <Spinner />
                {lastAction === 'submit' ? t('workspace.console.judgingHidden') : t('workspace.console.runningSamples')}
              </p>
            )}
            {showing?.status === 'error' && <p className="console-error">{showing.error}</p>}
            {showing?.status === 'done' && lastAction === 'run' && <RunResult run={showing} />}
            {showing?.status === 'done' && lastAction === 'submit' && <SubmitResult submission={showing} acceptedNote={acceptedNote} />}
          </>
        )}
      </div>
    </section>
  );
}
